import { useQuery } from '@tanstack/react-query';
import { GetAttachmentDownloadUrlInputSchema } from '@casetrace/shared';
import { getAttachmentDownloadUrl } from '../../lib/callables';

const URL_TTL_MS = 4 * 60 * 1000;

type Disposition = 'inline' | 'attachment';

interface UseAttachmentUrlParams {
  caseId: string;
  sourceItemId: string | undefined;
  enabled: boolean;
  disposition?: Disposition;
}

export function useAttachmentUrl(params: UseAttachmentUrlParams) {
  const { caseId, sourceItemId, enabled } = params;
  const disposition = params.disposition ?? 'inline';
  const shouldFetch = enabled && Boolean(caseId) && Boolean(sourceItemId);
  const query = useQuery({
    queryKey: ['attachment-url', caseId, sourceItemId, disposition] as const,
    queryFn: async () => {
      const input = GetAttachmentDownloadUrlInputSchema.parse({
        caseId,
        sourceItemId,
        disposition,
      });
      const result = await getAttachmentDownloadUrl(input);
      return { url: result.url, contentType: result.contentType };
    },
    enabled: shouldFetch,
    staleTime: URL_TTL_MS,
    retry: false,
  });

  return {
    url: query.data?.url ?? null,
    contentType: query.data?.contentType ?? null,
    isError: query.isError,
    isLoading: query.isLoading,
  };
}
